import React from "react";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.warn("[ErrorBoundary]", error?.message, info?.componentStack?.split("\n")[1]?.trim());
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="error-boundary" className="min-h-[60vh] grid place-items-center p-8">
          <div className="max-w-md text-center space-y-4">
            <div className="text-6xl font-black neon-text">!</div>
            <h2 className="text-xl font-bold">Qualcosa è andato storto</h2>
            <p className="text-sm text-white/60">
              La pagina ha incontrato un errore. Il resto del gestionale continua a funzionare.
            </p>
            <div className="flex gap-3 justify-center pt-2">
              <button onClick={this.reset} className="px-4 py-2 rounded-lg bg-[#ff0033] text-white text-sm">
                Riprova
              </button>
              <button onClick={() => (window.location.href = "/admin")} className="px-4 py-2 rounded-lg border border-white/20 text-sm">
                Vai alla dashboard
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Hook: esegue `fn` ogni `ms` finché la scheda è visibile e l'utente è loggato.
 * Si ferma automaticamente su `document.hidden` e riparte al ritorno.
 */
export function usePolling(fn, ms, { enabled = true } = {}) {
  const ref = React.useRef(fn);
  ref.current = fn;

  React.useEffect(() => {
    if (!enabled) return;
    let timer = null;
    const tick = async () => {
      if (document.hidden) return;
      try { await ref.current?.(); } catch { /* no-op */ }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tick, ms);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVis = () => {
      if (document.hidden) stop();
      else { tick(); start(); }
    };
    if (!document.hidden) { tick(); start(); }
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [ms, enabled]);
}
